import React, { useState } from "react";
import MatiereCard from "../components/MatiereCard";
import { createSearchParams, useNavigate, useParams } from "react-router-dom";
import { useGetMatieresWithDiagnosticQuery } from "redux_dir/features/matiereApi";
import { useGetDiagnostiqueTentativeByEleveQuery } from "redux_dir/features/diagnostiqueApi";
import Tentative from "pages/home/components/tentative";
import { useSelector } from "react-redux";
import { getCurrentEleve, getTentatives } from "redux_dir/features/eleveSlice";
import { progressImage } from "utils/images";
import { isSecondaire } from "utils/niveaux";
import { useDispatch } from "react-redux";
import { getParentForm } from "redux_dir/features/parentSlice";
import { ENABLE_CREDIT_SYSTEM } from "constants";
import { clearNote, setNewDiagnostic } from "redux_dir/features/diagnostiqueSlice";
import useGetUserInfo from "hooks/useGetUserInfo";
import { toast } from "sonner";
import Modal from "components/modal";
import { Loader, Plus, Camera } from "tabler-icons-react";
import useGetAssurance from "hooks/useGetAssurance";
import { IconArchive } from "@tabler/icons-react";
import { useGenerateQuizMutation, useResolveExerciseMutation } from "redux_dir/features/homeworkHelpApi";
import mixpanel from "mixpanel-browser";

function DiagnostiqueHistoriquePage() {
  const navigation = useNavigate();

  const { userId } = useGetUserInfo();

  const params = useParams();

  const dispatch = useDispatch();

  const formParent = useSelector(getParentForm);

  const [show, setShow] = useState(false);
  const [diagnostique, setDiagnostique] = useState(null);
  const [showHomeworkHelp, setShowHomeworkHelp] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showAideModal, setShowAideModal] = useState(false);

  const app = useGetAssurance();

  const studentDemo = useSelector(getCurrentEleve);

  // Élève réellement suivi : celui de l'URL, sinon l'élève courant du store.
  // (`useGetStudent` renvoyait un élève de démo codé en dur -> mauvaises URLs)
  const eleveId = params?.eleveId ?? studentDemo?.id;

  // Collège / lycée : pas de diagnostic de niveau, seulement une démo de Genimi
  const estSecondaire = isSecondaire(studentDemo?.classe);

  const [generateQuiz] = useGenerateQuizMutation();
  const [resolveExercise] = useResolveExerciseMutation();

  // Log pour déboguer le problème de classeId
  React.useEffect(() => {
    console.log('[DiagnostiqueHistoriquePage] studentDemo:', studentDemo);
    console.log('[DiagnostiqueHistoriquePage] classeId:', studentDemo?.classeId);
  }, [studentDemo]);

  const {
    data: matierDiagnosticData,
    isLoading,
    isError,
  } = useGetMatieresWithDiagnosticQuery(
    {
      classeId: studentDemo?.classeId,
    },
    {
      skip: !studentDemo?.classeId, // Ne pas faire la requête si classeId n'est pas disponible
    }
  );

  // Ordre d'affichage voulu : Mathématiques avant Lecture
  const allowedSubjects = ["MATHEMATIQUE", "LECTURE"];

  // Filter out Demo Diagnostics from Normal
  const filteredMatierDiagnosticData =
    formParent?.type === "CONFIRMED"
      ? matierDiagnosticData?.data?.filter(
        (item) => item?.type_diagnostic === "NORMAL"
      )
      : matierDiagnosticData?.data?.filter(
        (item) => item?.type_diagnostic === "DEMO"
      );

  const tentativeDemos = useSelector(getTentatives)?.filter(
    (tentative) => tentative?.eleveId === studentDemo?.id
  );

  const { data: diagnostiqueData } = useGetDiagnostiqueTentativeByEleveQuery(
    params?.eleveId
  );

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleHomeworkHelp = async (type) => {
    if (!selectedImage) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    setIsProcessing(true);
    try {
      if (type === "quiz") {
        const result = await generateQuiz(selectedImage).unwrap();
        // Handle quiz result
        console.log("Quiz result:", result);
      } else {
        const result = await resolveExercise(selectedImage).unwrap();
        // Handle exercise resolution result
        console.log("Exercise resolution result:", result);
      }
    } catch (error) {
      // Vérifier si c'est une erreur d'indisponibilité de l'IA
      if (error?.status === 503 || error?.data?.error === "AI_UNAVAILABLE") {
        toast.error("Assistant IA momentanément indisponible", {
          duration: 5000,
          style: {
            background: '#ef4444',
            color: '#fff',
          },
        });
      } else {
        toast.error("Une erreur s'est produite lors du traitement de l'image");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Recharger du credit
   */
  const handleSetCredt = async () => {
    mixpanel.track("Clic Recharger Crédit", {
      "Source": "Modal Diagnostic"
    });
    navigation("/payment");
  };

  /**
   *
   */
  /**
   *
   */
  // Un parent sur deux fermait la carte "Commencer le diagnostic" sans y
  // donner suite : elle n'ajoutait aucune information utile, juste un tap de
  // plus entre l'intention et l'activité. On n'affiche donc plus de
  // confirmation quand l'enfant a des crédits — sauf s'il n'en a plus, seul
  // cas où l'utilisateur a réellement besoin d'agir avant de continuer.
  const creditsBloquent =
    ENABLE_CREDIT_SYSTEM &&
    !(app === "Senassurance" && formParent?.type === "CONFIRMED") &&
    Number(formParent?.credits) <= 0;

  const goToDiagnostic = (item) => {
    dispatch(clearNote());

    navigation({
      pathname: `/${eleveId}/${item?.matiereId?.name?.toLowerCase()}/${item?.matiereId?.name?.toLowerCase() === "lecture"
        ? "lecture"
        : "math"
        }/${item?.diagnosticId}`,
      state: {
        eleveId: eleveId,
        randomImage: progressImage[parseInt(Math.random() * 6)],
      },
      search: `?${createSearchParams({
        niveau: 0,
        epreuve: 0,
      })}`,
    });
  };

  const handleSelectMatiere = (item) => {
    mixpanel.track("Sélection Matière", {
      "Matière": item?.matiereId?.name,
      "Type": "Diagnostic",
      "Enfant ID": eleveId,
    });

    dispatch(
      setNewDiagnostic({
        diagnosticId: item?.diagnosticId,
        studentId: eleveId,
        coachId: userId,
      })
    );

    if (creditsBloquent) {
      setDiagnostique(item);
      setShow(true);
      return;
    }

    mixpanel.track("Lancement Diagnostic", {
      "Matière": item?.matiereId?.name,
      "Enfant ID": eleveId,
      "Crédits Restants": formParent?.credits,
    });

    try {
      goToDiagnostic(item);
    } catch (error) {
      toast.error("Une erreur s'est produite");
    }
  };

  const handleStartAide = () => {
    mixpanel.track("Lancement Aide aux devoirs", {
      "Enfant ID": eleveId
    });
    navigation(`/${eleveId}/homework`);
  };

  const handleSelectAide = () => {
    mixpanel.track("Sélection Matière", {
      "Matière": "Aide aux devoirs",
      "Enfant ID": eleveId,
    });

    if (creditsBloquent) {
      setShowAideModal(true);
      return;
    }

    handleStartAide();
  };

  return (
    <div>
      {!studentDemo?.classeId ? (
        <div className="py-12 text-center">
          <Loader size={28} className="animate-spin mx-auto mb-3 text-secondary" />
          <p className="text-primary/60 text-sm">
            Chargement des informations de l'élève...
          </p>
        </div>
      ) : isLoading ? (
        <div className="py-12 text-center">
          <Loader size={28} className="animate-spin mx-auto mb-3 text-secondary" />
          <p className="text-primary/60 text-sm">Chargement des matières...</p>
        </div>
      ) : (
        <>
          <div className="px-2 pt-1 pb-3">
            <h2 className="font-extrabold text-primary text-[19px] leading-tight">
              Que faire aujourd'hui ?
            </h2>
            <p className="text-[13px] text-primary/50 mt-0.5">
              {estSecondaire
                ? "Découvrez comment Genimi accompagne votre enfant."
                : "Choisissez une matière pour lancer un diagnostic."}
            </p>
          </div>
          <div className="flex flex-col gap-3 px-2">
            {isError ? (
              <div className="bg-danger/10 text-danger border border-danger/20 rounded-2xl p-4 text-center text-sm">
                Erreur lors du chargement des matières
              </div>
            ) : (
              <>
                {filteredMatierDiagnosticData
                  ?.filter(item => allowedSubjects.includes(item?.matiereId?.name))
                  ?.slice()
                  ?.sort(
                    (a, b) =>
                      allowedSubjects.indexOf(a?.matiereId?.name) -
                      allowedSubjects.indexOf(b?.matiereId?.name)
                  )
                  ?.map((item, index) => (
                  <MatiereCard
                    key={index}
                    name={item?.matiereId?.name}
                    onClick={() => handleSelectMatiere(item)}
                    img={item?.matiereId?.name === "LECTURE" ? "book" : "math"}
                    classe={studentDemo?.classe}
                  />
                ))}
                <MatiereCard
                  name="Aide aux devoirs"
                  onClick={handleSelectAide}
                  img="camera"
                />
              </>
            )}
          </div>
        </>
      )}

      {/* Liste des tentatives */}
      <Tentative
        eleve={studentDemo}
        studentDemo={studentDemo}
        offlineData={tentativeDemos ?? null}
        data={diagnostiqueData ? diagnostiqueData?.data : []}
        onClick={(history) =>
          navigation("../lecture/analyse/" + history?.id, {
            state: {
              eleveId: eleveId,
              seuil: "70",
            },
          })
        }
      />

      {/* Modal for Homework Help */}
      <Modal
        opened={showHomeworkHelp}
        onClosed={() => setShowHomeworkHelp(false)}
      >
        <h1 className="font-bold text-xl text-center mb-4">
          Aide aux devoirs
        </h1>
        <div className="space-y-4">
          <div className="flex flex-col items-center">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload"
            />
            <label
              htmlFor="image-upload"
              className="cursor-pointer bg-primary text-white px-4 py-2 rounded-full flex items-center gap-2"
            >
              <Camera size={20} />
              Prendre une photo
            </label>
            {selectedImage && (
              <img
                src={selectedImage}
                alt="Selected"
                className="mt-4 max-w-full h-48 object-contain"
              />
            )}
          </div>
          <div className="flex gap-4 justify-center mt-4">
            <button
              onClick={() => handleHomeworkHelp("quiz")}
              disabled={!selectedImage || isProcessing}
              className="bg-primary text-white px-4 py-2 rounded-full flex items-center gap-2"
            >
              {isProcessing ? <Loader className="animate-spin" /> : "Générer un quiz"}
            </button>
            <button
              onClick={() => handleHomeworkHelp("resolution")}
              disabled={!selectedImage || isProcessing}
              className="bg-primary text-white px-4 py-2 rounded-full flex items-center gap-2"
            >
              {isProcessing ? <Loader className="animate-spin" /> : "Résoudre l'exercice"}
            </button>
          </div>
        </div>
      </Modal>

      {/*
        Crédits épuisés : c'est le SEUL cas où on interrompt encore le parent
        avant l'activité — il n'y a rien d'autre à faire que recharger.
        Dans tous les autres cas, les cartes ci-dessus ouvrent l'activité
        directement, sans cette étape de confirmation.
      */}
      <Modal
        opened={show || showAideModal}
        onClosed={() => setShow(false) || setShowAideModal(false)}
        data-testid="modal"
      >
        <h1 className="font-bold text-xl text-center">Crédits épuisés</h1>
        <h1 className="font-[500] text-md text-center my-5">
          Il vous reste{" "}
          <span className="text-2xl font-bold text-[green]">
            {formParent?.credits} points
          </span>{" "}
          <br />
          Vous ne pouvez plus continuer
          <br />
          Veuillez recharger vos crédits pour continuer.
        </h1>
        <button
          onClick={handleSetCredt}
          className="w-full bg-primary h-[48px] rounded-full text-white font-bold mt-6 flex items-center justify-center gap-3 active:scale-[0.98] transition-transform"
        >
          <Plus />
          Recharger du credit
        </button>
      </Modal>
    </div>
  );
}

export default DiagnostiqueHistoriquePage;
